// @flow
import React, { type Node, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import loadable from '@loadable/component';
import Nav from 'components/Nav';
import { HEADER_HEIGHT } from 'components/Nav/Desktop';
import { Provider } from 'components/PageContext';
import { Provider as CuisinesProvider } from 'components/CuisinesContext';
import ModalProvider from 'components/Modals/Provider';
import type { PageContextType } from 'components/PageContext';
import templateRequest from 'utils/templateRequest';
import * as componentKeys from 'componentKeys';

type PropsType = {
  children: Node,
  pageContext: PageContextType,
  path?: string
};

const PrivacySensitivePrompt = loadable(
  () => import('./PrivacySensitivePrompt'),
  { ssr: false }
);
const Footer = loadable(() => import('../Footer'));

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require, import/no-extraneous-dependencies
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React);
}

const Outer = styled.div`
  overflow: hidden;
  ${({ useTallHeader }) =>
    useTallHeader ? `padding-top: ${HEADER_HEIGHT}px;` : ''};
`;

const Content = styled.div`
  width: 100%;
  min-height: 100vh;
`;

export default function Layout({ path, pageContext, children }: PropsType) {
  const {
    autoModalConfig,
    story,
    rootSlug = '/',
    layout,
    header,
    footer,
    country,
    countries
  } = pageContext;
  const [customHeaderConfig, setCustomHeaderConfig] = useState();
  if (story.content && story.content.enable_shop_theme) {
    useEffect(() => {
      templateRequest('/v2/shops/{{shop}}/theme').then(({ data }) => {
        if (!data || !data.shop_theme) return;
        const {
          header_logo: headerLogo,
          use_tall_header: useTallHeader
        } = data.shop_theme;
        setCustomHeaderConfig({
          headerLogo,
          useTallHeader
        });
      });
    }, []);
  }

  const uniqueCountries = Array.from(
    new Map(countries.map((item) => [item.code, item])).values()
  );

  const supportedCountries = uniqueCountries.filter((locale) =>
    pageContext.links.alternates.find(
      (alternateLink) => locale.code === alternateLink.lang
    )
  );

  return (
    <Provider value={pageContext}>
      <CuisinesProvider>
        <ModalProvider
          autoModalConfig={autoModalConfig}
          currentStory={story.full_slug}
        >
          <Outer useTallHeader={customHeaderConfig?.useTallHeader}>
            <Nav
              isWhiteOnly={
                story.field_component === componentKeys.STATIC_CONTENT
              }
              path={path}
              homeUrl={rootSlug}
              country={country}
              header={header}
              countries={supportedCountries}
              countryLinks={pageContext.links}
              customHeaderConfig={customHeaderConfig}
            />
            <Content>{children}</Content>
            <Footer homeUrl={rootSlug} layout={layout} config={footer} />
            <PrivacySensitivePrompt pageContext={pageContext} />
          </Outer>
        </ModalProvider>
      </CuisinesProvider>
    </Provider>
  );
}
